export type MdInlineNode =
  | { type: 'text'; value: string }
  | { type: 'bold'; children: MdInlineNode[] }
  | { type: 'sup'; children: MdInlineNode[] }
  | { type: 'link'; url: string; children: MdInlineNode[] }

export type MdParagraph = {
  children: MdInlineNode[]
}

const textNode = (value: string): MdInlineNode => ({ type: 'text', value })
const boldNode = (children: MdInlineNode[]): MdInlineNode => ({ type: 'bold', children })
const supNode = (children: MdInlineNode[]): MdInlineNode => ({ type: 'sup', children })
const linkNode = (url: string, children: MdInlineNode[]): MdInlineNode => ({
  type: 'link',
  url,
  children,
})

const internalPath = /^\/(?![/\\])/
const anchor = /^#/
const absoluteHttp = /^https?:\/\//i
const mailto = /^mailto:/i
const allowedUrlPatterns = [internalPath, anchor, absoluteHttp, mailto]

export const isSafeUrl = (url: string): boolean =>
  allowedUrlPatterns.some((pattern) => pattern.test(url.trim()))

type InlineRule = {
  pattern: RegExp
  build: (match: RegExpExecArray) => MdInlineNode[]
}

const inlineRules: InlineRule[] = [
  {
    pattern: /\*\*([\s\S]+?)\*\*/,
    build: (match) => [boldNode(parseInline(match[1] ?? ''))],
  },
  {
    pattern: /\^([^^]+?)\^/,
    build: (match) => [supNode(parseInline(match[1] ?? ''))],
  },
  {
    pattern: /(?<!!)\[([^\]]+)\]\(([^)\s]+)\)/,
    build: (match) => {
      const [, label = '', url = ''] = match
      const children = parseInline(label)
      return isSafeUrl(url) ? [linkNode(url, children)] : children
    },
  },
]

type ConstructMatch = {
  start: number
  end: number
  nodes: MdInlineNode[]
}

const findNextConstruct = (text: string): ConstructMatch | null => {
  let leftmost: ConstructMatch | null = null

  for (const rule of inlineRules) {
    const match = rule.pattern.exec(text)
    if (!match || (leftmost && match.index >= leftmost.start)) {
      continue
    }

    leftmost = {
      start: match.index,
      end: match.index + match[0].length,
      nodes: rule.build(match),
    }
  }

  return leftmost
}

const parseInline = (text: string): MdInlineNode[] => {
  const nodes: MdInlineNode[] = []
  let rest = text

  while (rest.length > 0) {
    const construct = findNextConstruct(rest)

    if (!construct) {
      nodes.push(textNode(rest))
      break
    }

    if (construct.start > 0) {
      nodes.push(textNode(rest.slice(0, construct.start)))
    }
    nodes.push(...construct.nodes)
    rest = rest.slice(construct.end)
  }

  return nodes
}

export const parseMarkdown = (content: string): MdParagraph[] =>
  content.split('\n').map((line) => ({ children: parseInline(line) }))

const collectText = (nodes: MdInlineNode[]): string =>
  nodes.map((node) => (node.type === 'text' ? node.value : collectText(node.children))).join('')

export const treeToPlainText = (paragraphs: MdParagraph[]): string =>
  paragraphs.map((paragraph) => collectText(paragraph.children)).join(' ')

export const flattenToInline = (paragraphs: MdParagraph[]): MdInlineNode[] =>
  paragraphs.flatMap((paragraph, index) =>
    index === 0 ? paragraph.children : [textNode(' '), ...paragraph.children],
  )

export const truncateInline = (nodes: MdInlineNode[], maxChars: number): MdInlineNode[] => {
  let budget = maxChars

  const takeNode = (node: MdInlineNode): MdInlineNode | null => {
    if (node.type === 'text') {
      const slice = node.value.slice(0, budget)
      budget -= slice.length
      return slice.length > 0 ? textNode(slice) : null
    }

    const children = takeChildren(node.children)
    if (node.type === 'bold') {
      return boldNode(children)
    }
    if (node.type === 'sup') {
      return supNode(children)
    }
    return linkNode(node.url, children)
  }

  const takeChildren = (input: MdInlineNode[]): MdInlineNode[] => {
    const kept: MdInlineNode[] = []

    for (const node of input) {
      if (budget <= 0) {
        break
      }
      const taken = takeNode(node)
      if (taken) {
        kept.push(taken)
      }
    }

    return kept
  }

  return takeChildren(nodes)
}
